import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import '../Academy.css';

// Import images
import academyLogo from '../../assets/images/ProgenX Akademi.png';
import progenxakademiHeroImage from '../../assets/images/progenxakademi.jpg';
import webAppBadge from '../../assets/images/web-app-badge.png';
import googlePlayBadge from '../../assets/images/google-play-badge.png';
import appStoreBadge from '../../assets/images/app-store-badge.png';

const AcademyEN = () => {
  return (
    <div className="academy">
      <Helmet>
        <title>ProgenX Academy | AI-Powered Personalized Education Platform 2025</title>
        <meta name="description" content="ProgenX Academy is an AI-powered personalized education platform. We make a difference in education with special programs prepared according to students' individual needs, forgetting curve-based feedback, and AI-powered question solutions. Solutions suitable for every level with LGS, YKS, 5-7th grade packages." />
        <meta name="keywords" content="progenx academy, ai education, personalized education, education platform, ai question solving, ai in education, lgs preparation, yks preparation, online education, smart learning, forgetting curve, adaptive learning" />
        <meta property="og:title" content="ProgenX Academy | AI-Powered Education Platform" />
        <meta property="og:description" content="AI-powered personalized learning experience. Educational programs optimized according to each student's individual needs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/en/academy" />
        <meta property="og:image" content="https://progenxacademy.com/progenxakademi.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ProgenX Academy | AI-Powered Education Platform" />
        <meta name="twitter:description" content="Education platform offering AI-powered personalized learning experience." />
        <link rel="canonical" href="https://progenxacademy.com/en/academy" />
        <link rel="alternate" hreflang="tr" href="https://progenxacademy.com/progenxakademi" />
        <link rel="alternate" hreflang="en" href="https://progenxacademy.com/en/academy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ProgenX Academy",
            "description": "AI-powered personalized education platform",
            "url": "https://app.progenxacademy.com",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web, iOS, Android",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "TRY"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "ratingCount": "1000"
            },
            "provider": {
              "@type": "Organization",
              "name": "ProgenX",
              "url": "https://progenxacademy.com"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is ProgenX Academy and how does it work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX Academy is an AI-powered personalized learning platform. It creates special educational programs optimized according to students' individual learning speeds, strengths, and weaknesses. The platform analyzes user data to offer students special learning materials and content."
                }
              },
              {
                "@type": "Question",
                "name": "What grade levels are packages available for?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX Academy offers special packages for 5-7th grades, 8th grade (LGS 2026), and 9-12th grades (YKS 2026). Each package contains content and features suitable for the relevant grade level."
                }
              },
              {
                "@type": "Question",
                "name": "How does AI-powered question solving work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "AI-powered systems that show solutions to questions students struggle with combine with teacher-approved solutions to offer high accuracy rates. Detailed explanations and similar question suggestions are provided for each question."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        title="PROGENX ACADEMY" 
        backgroundImage={progenxakademiHeroImage}
      />
      
      <section className="academy-content">
        <div className="container">
          <div className="academy-header">
            <img src={academyLogo} alt="ProgenX Academy Logo" className="academy-logo" />
            <h2 className="section-title">AI-Powered Education Platform</h2>
          </div>
          
          <div className="academy-description">
            <p>
              ProgenX is an AI-powered platform that aims to ensure equal opportunities in education and increase students' academic success by offering personalized solutions according to each student's individual learning needs. It steps in where traditional education methods fall short, aiming to maximize students' individual potential. Our goal is to accelerate and make each student's learning process more efficient by offering personalized learning experiences based on strengths and weaknesses.
            </p>
            
            <p>
              The ProgenX platform analyzes user data to offer students special learning materials and content, thus ensuring each student learns in the most efficient way. The features offered by our project support individualization in education while also developing solutions to increase students' motivation.
            </p>
          </div>
          
          <div className="academy-features">
            <h3 className="features-title">Features of Our Platform</h3>
            
            <div className="features-grid">
              <div className="feature-item">
                <h4>Personalized Education Plans</h4>
                <p>Creates special educational programs optimized according to students' individual learning speeds, strengths, and weaknesses.</p>
              </div>
              
              <div className="feature-item">
                <h4>Forgetting Curve-Based Feedback</h4>
                <p>AI-powered algorithms determine topics students need to review according to their forgetting curve and suggest special tests for them. This way, information becomes permanent.</p>
              </div>
              
              <div className="feature-item">
                <h4>AI-Powered Question Solving</h4>
                <p>AI-powered systems that show solutions to questions students struggle with combine with teacher-approved solutions to offer high accuracy rates.</p>
              </div>
              
              <div className="feature-item">
                <h4>Motivation and Development Support</h4>
                <p>Offers mechanisms including personal success charts and instant feedback to increase students' motivation.</p>
              </div>
            </div>
          </div>
          
          <div className="academy-packages">
            <h3 className="packages-title">Our Packages</h3>
            <p className="packages-subtitle">We offer solutions suitable for your needs</p>
            
            <div className="packages-row">
              <div className="package-column">
                <div className="package-card">
                  <h4 className="package-title">5th, 6th, and 7th Grades</h4>
                  <ul className="package-features">
                    <li>Monthly 100 question asking rights (AI-powered)</li>
                    <li>Level-based smart question suggestions</li>
                    <li>100,000+ question pool, content from more than 10 publishers</li>
                    <li>Personalized monthly and weekly study plans</li>
                    <li>Personalized topic deficiency report every week</li>
                    <li>Parent-specific development panel</li>
                    <li>Ask teacher system</li>
                    <li>Content matching according to student level</li>
                    <li>Gradual discount advantage for those who renew membership each year</li>
                  </ul>
                  <div className="package-cta">
                    <a href="https://app.progenxacademy.com/login" 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="btn btn-primary">Login</a>
                  </div>
                </div>
              </div>
              
              <div className="package-column">
                <div className="package-card">
                  <h4 className="package-title">YKS 2026 (9th, 10th, 11th, 12th Grades)</h4>
                  <p className="package-subtitle">TYT - AYT Preparation 2026 Period</p>
                  <ul className="package-features">
                    <li>Monthly 150 question asking rights (AI-powered)</li>
                    <li>Goal-based personalized study plans</li>
                    <li>200,000+ questions, more than 200 books</li>
                    <li>Topic screening tests, nationwide practice exams</li>
                    <li>Advanced deficiency analysis system</li>
                    <li>Student performance tracking and parent access panel</li>
                    <li>Ask teacher system (Unlimited)</li>
                    <li>Gradual discount advantage for those who renew membership each year</li>
                  </ul>
                  <div className="package-cta">
                    <a href="https://app.progenxacademy.com/login" 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="btn btn-primary">Login</a>
                  </div>
                </div>
              </div>

              <div className="package-column">
                <div className="package-card">
                  <h4 className="package-title">LGS 2026 (8th Grade)</h4>
                  <p className="package-subtitle">Special for 2026 Exam Period</p>
                  <ul className="package-features">
                    <li>Monthly 150 question asking rights (AI-powered)</li>
                    <li>Level-based smart question suggestions</li>
                    <li>100,000+ question pool, content from more than 10 publishers</li>
                    <li>Personalized monthly and weekly study plans</li>
                    <li>Personalized topic deficiency report every week</li>
                    <li>Nationwide practice exams + topic screening tests</li>
                    <li>Gift study materials</li>
                    <li>Parent-specific development panel</li>
                    <li>Ask teacher system</li>
                    <li>Content matching according to student level</li>
                    <li>Gradual discount advantage for those who renew membership each year</li>
                  </ul>
                  <div className="package-cta">
                    <a href="https://app.progenxacademy.com/login" 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="btn btn-primary">Login</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="academy-faq">
            <h3 className="section-title">Frequently Asked Questions</h3>
            <div className="faq-container">
              <div className="faq-item">
                <h4 className="faq-question">What is ProgenX Academy and how does it work?</h4>
                <p className="faq-answer">
                  ProgenX Academy is an AI-powered personalized learning platform. It creates special educational programs optimized according to students' individual learning speeds, strengths, and weaknesses. The platform analyzes user data to offer students special learning materials and content.
                </p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">What grade levels are packages available for?</h4>
                <p className="faq-answer">
                  ProgenX Academy offers special packages for 5-7th grades, 8th grade (LGS 2026), and 9-12th grades (YKS 2026). Each package contains content and features suitable for the relevant grade level.
                </p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">How does AI-powered question solving work?</h4>
                <p className="faq-answer">
                  AI-powered systems that show solutions to questions students struggle with combine with teacher-approved solutions to offer high accuracy rates. Detailed explanations and similar question suggestions are provided for each question.
                </p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">What is forgetting curve-based feedback?</h4>
                <p className="faq-answer">
                  AI-powered algorithms determine topics students need to review according to their forgetting curve and suggest special tests for them. This way, information becomes permanent and learning efficiency increases.
                </p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">Which devices can I access from?</h4>
                <p className="faq-answer">
                  You can access ProgenX Academy from computers, tablets, and smartphones. You can use it through a web browser or via iOS and Android mobile applications.
                </p>
              </div>
              <div className="faq-item">
                <h4 className="faq-question">Are there parent and teacher panels?</h4>
                <p className="faq-answer">
                  Yes, ProgenX Academy has a parent-specific development panel and an ask teacher system. Parents can track student progress, and teachers can provide support to their students.
                </p>
              </div>
            </div>
          </section>
          
          <div className="app-download-section">
            <h3 className="section-title">Access Our Application Now</h3>
            <p className="app-download-subtitle">Whether from your web browser or mobile device - ProgenX is always with you!</p>
            
            <div className="app-badges">
              <a href="https://app.progenxacademy.com/" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="badge-link">
                <img src={webAppBadge} 
                     alt="Web Application" 
                     className="app-badge" />
              </a>
              
              <a href="https://play.google.com/store/apps/details?id=com.ProgenxAcademy.progenx" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="badge-link">
                <img src={googlePlayBadge} 
                     alt="Download from Google Play" 
                     className="app-badge" />
              </a>
              
              <a href="https://apps.apple.com/tr/app/progenx/id6744415621?l=tr" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="badge-link">
                <img src={appStoreBadge} 
                     alt="Download from App Store" 
                     className="app-badge" />
              </a>
            </div>
          </div>

        </div>
      </section>
      
      <CallToAction 
        title="The AI Revolution in Education is Starting!"
        subtitle="Join us to discover the education technologies of the future."
        buttonText="Contact Us" 
        buttonLink="/en/contact" 
      />
    </div>
  );
};

export default AcademyEN;
